import type { JSX } from 'react'
import { Check } from 'lucide-react'
import { cn } from '@/lib/cn'

/**
 * Кнопка-переключатель для наборов («какие движения разрешены», «какие дни
 * недели», «что не есть»).
 *
 * Почему не `Switch` из примитивов: набор из пяти-девяти тумблеров занимает
 * полэкрана и читается как список независимых настроек, хотя это одно поле с
 * множественным выбором. Здесь же выбранное видно одним взглядом.
 *
 * Доступность: `aria-pressed` (состояние читается скринридером), выбранность
 * несёт не только цвет — появляется галочка; доступное имя можно задать
 * отдельно от короткой видимой подписи («пн» → «понедельник»).
 */
export interface ToggleButtonProps {
  pressed: boolean
  onPressedChange: (pressed: boolean) => void
  /** Видимая подпись. */
  children: string
  /** Доступное имя, если видимая подпись сокращённая. */
  name?: string
  disabled?: boolean
  /** Галочка у выбранного варианта. Для узких кнопок (дни недели) не нужна. */
  showCheck?: boolean
  className?: string
}

export function ToggleButton({
  pressed,
  onPressedChange,
  children,
  name,
  disabled = false,
  showCheck = true,
  className
}: ToggleButtonProps): JSX.Element {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      aria-label={name}
      disabled={disabled}
      onClick={() => onPressedChange(!pressed)}
      className={cn(
        'press inline-flex h-8 items-center gap-1.5 rounded-sm border px-2.5 text-xs',
        'disabled:cursor-not-allowed disabled:opacity-45',
        pressed
          ? 'border-honey/70 bg-honey/18 text-honey'
          : 'border-line bg-panel-3 text-ink-muted hover:border-line-strong hover:text-ink',
        className
      )}
    >
      {showCheck ? (
        <Check aria-hidden className={cn('size-3.5 shrink-0', pressed ? '' : 'opacity-0')} />
      ) : null}
      {children}
    </button>
  )
}
